<script setup lang="ts">
import type { Job } from '@/types/model'
import CardInfo from '@/components/card/CardInfo.vue'
import LabelDesc from '@/components/label/LabelDesc.vue'
import type { ColorKey } from '@/types/tailwind'
import { iconJobType } from '@/globals/constants.ts'
import { getInputTime } from '@/globals/time.ts'
import { technicianName } from '@/globals/constants.ts'
import LabelBase from '@/components/label/LabelBase.vue'
import { formatNote } from '@/globals/text.ts'
import BadgeIcon from '@/components/badge/BadgeIcon.vue'

defineProps<{
  job: Job
}>()
</script>

<template>
  <card-info
    title="Information"
    desc="Data ini yang nanti akan digunakan saat memberikan informasi tiket perbaikan"
    :color="(job.job_types?.color as ColorKey) ?? 'blue'"
    :icon="iconJobType[job.job_type ?? 0]"
  >
    <div class="space-y-4">
      <label-desc title="no. job">
        <label-base class="block font-mono">{{ job.name }}</label-base>
        <label-base v-if="job.tickets" size="sm" color="stone" class="block font-mono">{{
          job.tickets?.name
        }}</label-base>
      </label-desc>

      <label-desc title="job type" :color="job.job_types?.color as ColorKey">{{
        job.job_types?.name ?? '-'
      }}</label-desc>
      <label-desc title="status" :color="job.states?.color as ColorKey">{{
        job.states?.name ?? '-'
      }}</label-desc>

      <div v-if="job.branch_services || job.handle_by" class="grid grid-cols-2 gap-4">
        <label-desc v-if="job.branch_services" title="handle by">{{
          job.branch_services?.name ?? '-'
        }}</label-desc>
        <label-desc v-if="job.handle_by" title="technician">{{
          technicianName[job.handle_by] ?? '-'
        }}</label-desc>
      </div>

      <label-desc v-if="job.note" title="note">
        <div
          v-html="formatNote(job.note, job.states?.color)"
          class="text-sm whitespace-pre-wrap"
        ></div>
      </label-desc>
    </div>
  </card-info>

  <card-info
    title="Customer Information"
    desc="Data konsumen yang akan dikunjungi"
    :color="(job.customer_types?.color as ColorKey) ?? 'stone'"
    :icon="job.customer_type === 1 ? 'users' : 'warehouse'"
  >
    <div class="space-y-4">
      <label-desc title="customer name">
        <label-base class="block">{{ job.customer_name }}</label-base>
        <label-base
          v-if="job.customer_types"
          size="sm"
          :color="(job.customer_types?.color as ColorKey) ?? 'stone'"
          class="block"
          >{{ job.customer_types?.name ?? '-' }}</label-base
        >
      </label-desc>
      <label-desc title="mobile phone">
        <label-base class="font-mono">{{ job.phone }}</label-base>
      </label-desc>
      <label-desc v-if="job.phone2" title="home phone">
        <label-base class="font-mono">{{ job.phone2 }}</label-base>
      </label-desc>
      <label-desc v-if="job.phone3" title="business phone">
        <label-base class="font-mono">{{ job.phone3 }}</label-base>
      </label-desc>
      <label-desc title="address">{{ job.address }}</label-desc>
      <label-desc v-if="job.email" title="email">{{ job.email }}</label-desc>
      <label-desc v-if="job.tax_id" title="NIK / NPWP">
        <label-base class="font-mono">{{ job.tax_id }}</label-base>
      </label-desc>
    </div>
  </card-info>

  <card-info
    title="Product Information"
    desc="Data produk yang bermasalah"
    :color="job.job_types?.color as ColorKey"
    icon="robot"
  >
    <div class="space-y-4">
      <label-desc title="model">
        <label-base class="block font-mono">{{ job.model }}</label-base>
        <label-base v-if="job.models" color="stone" size="sm" class="block">{{
          job.models?.desc
        }}</label-base>
      </label-desc>

      <label-desc title="serial">
        <label-base class="font-mono">{{ job.serial }}</label-base>
      </label-desc>

      <label-desc v-if="job.warranty_no" title="No. Warranty">
        <label-base class="font-mono">{{ job.warranty_no }}</label-base>
      </label-desc>

      <label-desc v-if="job.purchase_date && job.warranty_type === 1" title="Purchase Date">
        <label-base class="font-mono">{{ getInputTime(job.purchase_date) }}</label-base>
      </label-desc>

      <label-desc v-if="job.accessories" title="accessories">{{ job.accessories }}</label-desc>
    </div>
  </card-info>

  <card-info
    title="Service Information"
    desc="Data perbaikan"
    :color="job.job_types?.color as ColorKey"
    icon="screwdriver"
  >
    <div class="space-y-4">
      <label-desc title="service info">{{ job.service_info }}</label-desc>
      <label-desc title="repair info">{{ job.repair_info }}</label-desc>
    </div>
  </card-info>

  <card-info
    title="Date Information"
    desc="Tanggal pelaksanaan"
    :color="job.job_types?.color as ColorKey"
    icon="clock"
  >
    <div class="grid grid-cols-2 gap-4">
      <div class="space-y-4">
        <label-desc title="created at" class="font-mono">{{
          getInputTime(job.created_at ?? '', true)
        }}</label-desc>
        <label-desc title="repair at" class="font-mono">{{
          getInputTime(job.repair_at ?? '', true)
        }}</label-desc>
        <label-desc title="collection at" class="font-mono">{{
          getInputTime(job.collection_at ?? '', true)
        }}</label-desc>
      </div>

      <div v-if="job.job_type === 2" class="space-y-4">
        <label-desc title="actual start at" class="font-mono">{{
          getInputTime(job.actual_start_at ?? '', true)
        }}</label-desc>
        <label-desc title="actual end at" class="font-mono">{{
          getInputTime(job.actual_end_at ?? '', true)
        }}</label-desc>
      </div>
    </div>
  </card-info>

  <card-info title="Misc Information" desc="Informasi tambahan">
    <div class="flex gap-2">
      <badge-icon
        icon="star"
        :color="job.quality_report ? 'purple' : 'stone'"
        :title="job.quality_report ? 'Qualified' : 'Not Qualified'"
      ></badge-icon>
      <badge-icon
        icon="printer"
        :color="job.warranty_label ? 'amber' : 'stone'"
        :title="job.warranty_label ? 'Warranty Printed' : 'Warranty Not Yet Printed'"
      ></badge-icon>
      <badge-icon
        icon="printer"
        :color="job.job_label ? 'lime' : 'stone'"
        :title="job.job_label ? 'Job Label Printed' : 'Job Label Not Yet Printed'"
      ></badge-icon>
    </div>
  </card-info>
</template>

<style scoped></style>
